import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { toast } from "sonner";
import { api, errMsg } from "../api";
import { durationLabel } from "./ParkingGrid";
import { FormField, PrimaryButton, SecondaryButton } from "./FormField";
import { useRequireAuth } from "../hooks/useRequireAuth";

const useAction = (refresh, reset) => {
  const [busy, setBusy] = useState(false);
  const requireAuth = useRequireAuth();
  const run = async (fn, msg) => {
    if (!requireAuth()) return;
    setBusy(true);
    try {
      const res = await fn();
      toast.success(msg(res));
      reset();
      await refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, run };
};

const StatusDisplay = ({ selected, space, now }) => (
  <div>
    <h3 className="text-xl font-semibold">Operations</h3>
    <p className="text-sm text-[#A3A3A3] mt-1" data-testid="selected-space-info">
      {selected
        ? `Space ${String(selected).padStart(2, "0")} · ${space?.status}${
            space?.assigned_to ? ` · owner ${space.assigned_to}` : ""
          }${space?.since && !space?.assigned_to ? ` · ${durationLabel(space.since, now)}` : ""}`
        : "No space selected — actions use the first free space"}
    </p>
  </div>
);

const CheckInTab = ({ fields, set, selected, busy, run }) => (
  <TabsContent value="checkin" className="flex flex-col gap-4 pt-6">
    <FormField
      label="Unit Number"
      data-testid="checkin-plate-input"
      value={fields.plate}
      onChange={(e) => set("plate", e.target.value)}
      placeholder="ABC-1234"
    />
    <FormField
      label="Driver (optional)"
      data-testid="checkin-driver-input"
      value={fields.driver}
      onChange={(e) => set("driver", e.target.value)}
      placeholder="Jane Doe"
    />
    <PrimaryButton
      data-testid="checkin-btn"
      disabled={busy}
      onClick={() =>
        run(
          () => api.checkin({ plate: fields.plate, driver: fields.driver || null, number: selected }),
          (s) => `Checked in at space ${s.number}`
        )
      }
    >
      Check In Vehicle
    </PrimaryButton>
  </TabsContent>
);

const CheckOutTab = ({ selected, busy, run }) => (
  <TabsContent value="checkout" className="flex flex-col gap-4 pt-6">
    <p className="text-sm text-[#A3A3A3]">Select an occupied space in the grid, then release it.</p>
    <PrimaryButton
      data-testid="checkout-btn"
      disabled={busy || !selected}
      onClick={() => run(() => api.checkout(selected), (s) => `Space ${s.number} freed`)}
    >
      Check Out Space {selected ? String(selected).padStart(2, "0") : ""}
    </PrimaryButton>
  </TabsContent>
);

const BookTab = ({ fields, set, selected, busy, run }) => (
  <TabsContent value="book" className="flex flex-col gap-4 pt-6">
    <FormField
      label="Driver Name"
      data-testid="book-driver-input"
      value={fields.driver}
      onChange={(e) => set("driver", e.target.value)}
      placeholder="Jane Doe"
    />
    <FormField
      label="Unit Number (optional)"
      data-testid="book-plate-input"
      value={fields.plate}
      onChange={(e) => set("plate", e.target.value)}
      placeholder="ABC-1234"
    />
    <PrimaryButton
      data-testid="book-btn"
      disabled={busy}
      onClick={() =>
        run(
          () => api.reserve({ driver: fields.driver, plate: fields.plate || null, number: selected }),
          (s) => `Space ${s.number} reserved`
        )
      }
    >
      Reserve Space
    </PrimaryButton>
    <SecondaryButton
      data-testid="cancel-reservation-btn"
      disabled={busy || !selected}
      onClick={() => run(() => api.release(selected), (s) => `Reservation on ${s.number} cancelled`)}
    >
      Cancel Reservation
    </SecondaryButton>
  </TabsContent>
);

const AssignTab = ({ fields, set, selected, busy, run }) => (
  <TabsContent value="assign" className="flex flex-col gap-4 pt-6">
    <FormField
      label="Owner Name"
      data-testid="assign-owner-input"
      value={fields.owner}
      onChange={(e) => set("owner", e.target.value)}
      placeholder="Permanent holder"
    />
    <FormField
      label="Unit Number (optional)"
      data-testid="assign-plate-input"
      value={fields.plate}
      onChange={(e) => set("plate", e.target.value)}
      placeholder="ABC-1234"
    />
    <PrimaryButton
      data-testid="assign-btn"
      disabled={busy || !selected}
      onClick={() =>
        run(
          () => api.assign({ number: selected, owner: fields.owner, plate: fields.plate || null }),
          (s) => `Space ${s.number} assigned to ${s.assigned_to}`
        )
      }
    >
      Assign Permanently
    </PrimaryButton>
    <SecondaryButton
      data-testid="unassign-btn"
      disabled={busy || !selected}
      onClick={() => run(() => api.unassign(selected), (s) => `Space ${s.number} unassigned`)}
    >
      Remove Assignment
    </SecondaryButton>
  </TabsContent>
);

const EMPTY = { plate: "", driver: "", owner: "" };

export const ActionPanel = ({ selected, refresh, space, now }) => {
  const [fields, setFields] = useState(EMPTY);
  const set = (key, value) => setFields((f) => ({ ...f, [key]: value }));
  const { busy, run } = useAction(refresh, () => setFields(EMPTY));
  const tabProps = { fields, set, selected, busy, run };

  return (
    <div className="p-6 border border-[#262626] bg-[#141414] flex flex-col gap-6" data-testid="action-panel">
      <StatusDisplay selected={selected} space={space} now={now} />
      <Tabs defaultValue="checkin">
        <TabsList className="grid grid-cols-4 bg-[#0A0A0A] rounded-sm">
          <TabsTrigger value="checkin" data-testid="tab-checkin" className="text-xs">In</TabsTrigger>
          <TabsTrigger value="checkout" data-testid="tab-checkout" className="text-xs">Out</TabsTrigger>
          <TabsTrigger value="book" data-testid="tab-book" className="text-xs">Book</TabsTrigger>
          <TabsTrigger value="assign" data-testid="tab-assign" className="text-xs">Assign</TabsTrigger>
        </TabsList>
        <CheckInTab {...tabProps} />
        <CheckOutTab {...tabProps} />
        <BookTab {...tabProps} />
        <AssignTab {...tabProps} />
      </Tabs>
    </div>
  );
};
